import { supabase } from '../config/supabase';

export type RatioKey = 'jotform_invitation' | 'jotform_enrolled' | 'hire_stages' | 'coded_stages';

export interface HireCodeRatioSetting {
  id: string;
  org_id: number;
  ratio_key: RatioKey;
  progress_values: number[];
  created_at: string;
  updated_at: string;
  created_by?: string | null;
}

export interface HireCodeRatioConfig {
  jotform_invitation: number[];
  jotform_enrolled: number[];
  hire_stages: number[];
  coded_stages: number[];
}

const DEFAULT_CONFIG: HireCodeRatioConfig = {
  jotform_invitation: [0, 1],
  jotform_enrolled: [2],
  hire_stages: [2, 3, 4, 5],
  coded_stages: [6],
};

export const RATIO_GROUP_LABELS: Record<RatioKey, string> = {
  jotform_invitation: 'Jotform Invitation Stages',
  jotform_enrolled: 'Jotform Enrolled Stages',
  hire_stages: 'Hire Stages',
  coded_stages: 'Coded Stages',
};

export const RATIO_GROUP_DESCRIPTIONS: Record<RatioKey, string> = {
  jotform_invitation: 'Stages counted as "invitations" in the Jotform to Hire Ratio',
  jotform_enrolled: 'Stages counted as "enrolled/hired" in the Jotform to Hire Ratio',
  hire_stages: 'Stages counted as "hires" in the Hire per Code Ratio',
  coded_stages: 'Stages counted as "coded" in the Hire per Code Ratio',
};

export const RATIO_KEYS: RatioKey[] = ['jotform_invitation', 'jotform_enrolled', 'hire_stages', 'coded_stages'];

function rowsToConfig(rows: HireCodeRatioSetting[]): HireCodeRatioConfig {
  const config: HireCodeRatioConfig = { ...DEFAULT_CONFIG };
  for (const row of rows) {
    if (row.ratio_key in config) {
      config[row.ratio_key as RatioKey] = row.progress_values;
    }
  }
  return config;
}

export const hireCodeRatioSettingsService = {
  getDefaultConfig(): HireCodeRatioConfig {
    return { ...DEFAULT_CONFIG };
  },

  async getSettings(orgId: number): Promise<HireCodeRatioConfig> {
    try {
      const { data, error } = await supabase
        .from('hire_code_ratio_settings')
        .select('*')
        .eq('org_id', orgId);

      if (error && error.code !== 'PGRST116') {
        console.warn('Error fetching hire code ratio settings; using defaults:', error);
        return { ...DEFAULT_CONFIG };
      }

      if (data && data.length > 0) {
        return rowsToConfig(data as HireCodeRatioSetting[]);
      }

      return { ...DEFAULT_CONFIG };
    } catch (e) {
      console.warn('Error in getSettings; using defaults:', e);
      return { ...DEFAULT_CONFIG };
    }
  },

  async getCurrentUserSettings(): Promise<HireCodeRatioConfig> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return { ...DEFAULT_CONFIG };
      const { data: profile } = await supabase
        .from('profiles')
        .select('org_id')
        .eq('id', user.id)
        .maybeSingle();
      if (!profile?.org_id) return { ...DEFAULT_CONFIG };
      return this.getSettings(Number(profile.org_id));
    } catch {
      return { ...DEFAULT_CONFIG };
    }
  },

  async upsertSetting(orgId: number, ratioKey: RatioKey, progressValues: number[], userId?: string): Promise<void> {
    const { error } = await supabase
      .from('hire_code_ratio_settings')
      .upsert({
        org_id: orgId,
        ratio_key: ratioKey,
        progress_values: progressValues,
        updated_at: new Date().toISOString(),
        created_by: userId || null,
      }, {
        onConflict: 'org_id,ratio_key'
      });
    if (error) throw error;
  },

  async replaceAllSettings(orgId: number, config: HireCodeRatioConfig): Promise<void> {
    const { error: delErr } = await supabase
      .from('hire_code_ratio_settings')
      .delete()
      .eq('org_id', orgId);
    if (delErr) throw delErr;

    const payload = RATIO_KEYS.map(key => ({
      org_id: orgId,
      ratio_key: key,
      progress_values: config[key],
      updated_at: new Date().toISOString(),
    }));

    const { error: insErr } = await supabase
      .from('hire_code_ratio_settings')
      .insert(payload);
    if (insErr) throw insErr;
  },

  async hasCustomSettings(orgId: number): Promise<boolean> {
    const { data } = await supabase
      .from('hire_code_ratio_settings')
      .select('id')
      .eq('org_id', orgId)
      .limit(1)
      .maybeSingle();
    return !!data;
  },
};
